import type {
  FinanceMarginDriverGrouping,
  FinanceMarginDriverThresholds,
  FinanceMarginDriversAnalysis,
  FinanceMarginDriversInsight,
  FinanceProjectDurationBucket,
  FinanceProjectMarginAnalysisBasis,
  FinanceProjectMarginBucket,
  FinanceProjectRateBucket,
  FinanceProjectSizeBucket,
  FinanceRollupSummary,
  ProjectMarginAnalyticsSummary,
  Task,
  TaskFinancialState,
} from "../models/production";

export interface MarginDriversProjectInput {
  projectId: string;
  projectName: string;
  currency?: string;
  updatedAt?: string;
  bidDate?: string;
  projectStartEst?: string;
  projectEndEst?: string;
  rollup?: FinanceRollupSummary;
  tasks: Array<{ task: Task; financialState?: TaskFinancialState }>;
}

export const DEFAULT_MARGIN_DRIVER_THRESHOLDS: FinanceMarginDriverThresholds = {
  sizeByBidValue: {
    medium: 50000,
    large: 150000,
  },
  sizeByTaskCount: {
    medium: 75,
    large: 200,
  },
  durationDays: {
    medium: 45,
    long: 120,
  },
  ratePerDay: {
    mid: 750,
    high: 850,
  },
  marginPct: {
    low: 10,
    high: 25,
  },
};

const DEPARTMENT_BY_TASK_TYPE: Record<string, string> = {
  supervisor: "Production",
  review: "Production",
  concept: "Matte Painting",
  dmp: "Matte Painting",
  comp: "Comp",
  roto: "Vendor",
  prep: "Vendor",
  gfx: "Graphics",
  cameratr: "Matchmove",
  cameramm: "Matchmove",
  objecttr: "Matchmove",
  objectmm: "Matchmove",
  ranim: "Matchmove",
  layout: "Layout",
  anim: "Animation",
  light: "Lighting",
  fx: "FX",
  model: "Asset Build",
  rig: "Asset Build",
  retop: "Asset Build",
  cloth: "Asset Build",
  texture: "Asset Build",
  photoscan: "Asset Build",
  fur: "Groom",
  groom: "Groom",
  lookdev: "Lookdev",
};

function roundMoney(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 100) / 100;
}

function roundPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 10) / 10;
}

function normalizeTaskType(value?: string | null) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function resolveDepartment(task: Task) {
  const mapped = DEPARTMENT_BY_TASK_TYPE[normalizeTaskType(task.taskType || task.pipelineStep)];
  if (mapped) return mapped;
  const raw = String(task.pipelineStep || task.taskType || "").trim();
  return raw || "Unmapped";
}

function parseDate(value?: string) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date : undefined;
}

function diffDaysInclusive(start?: Date, end?: Date) {
  if (!start || !end) return undefined;
  const diff = end.getTime() - start.getTime();
  if (!Number.isFinite(diff) || diff < 0) return undefined;
  return Math.max(1, Math.round(diff / 86400000) + 1);
}

function average(values: number[]) {
  if (!values.length) return undefined;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function variance(values: number[]) {
  if (values.length < 2) return undefined;
  const mean = average(values);
  if (typeof mean !== "number") return undefined;
  const result = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return roundPercent(result);
}

function correlation(xs: number[], ys: number[]) {
  if (xs.length < 3 || ys.length !== xs.length) return undefined;
  const meanX = average(xs);
  const meanY = average(ys);
  if (typeof meanX !== "number" || typeof meanY !== "number") return undefined;
  let numerator = 0;
  let denomX = 0;
  let denomY = 0;
  for (let index = 0; index < xs.length; index += 1) {
    const dx = xs[index] - meanX;
    const dy = ys[index] - meanY;
    numerator += dx * dy;
    denomX += dx * dx;
    denomY += dy * dy;
  }
  if (denomX <= 0 || denomY <= 0) return undefined;
  return Math.round((numerator / Math.sqrt(denomX * denomY)) * 100) / 100;
}

function bucketBySize(
  totalBidValue: number,
  taskCount: number,
  thresholds: FinanceMarginDriverThresholds
): FinanceProjectSizeBucket {
  if (totalBidValue >= thresholds.sizeByBidValue.large || taskCount >= thresholds.sizeByTaskCount.large) return "large";
  if (totalBidValue >= thresholds.sizeByBidValue.medium || taskCount >= thresholds.sizeByTaskCount.medium) return "medium";
  return "small";
}

function bucketByDuration(durationDays: number | undefined, thresholds: FinanceMarginDriverThresholds): FinanceProjectDurationBucket {
  if (!durationDays) return "medium";
  if (durationDays >= thresholds.durationDays.long) return "long";
  if (durationDays >= thresholds.durationDays.medium) return "medium";
  return "short";
}

function bucketByRate(ratePerDay: number | undefined, thresholds: FinanceMarginDriverThresholds): FinanceProjectRateBucket {
  if (typeof ratePerDay !== "number") return "mid";
  if (ratePerDay >= thresholds.ratePerDay.high) return "high";
  if (ratePerDay >= thresholds.ratePerDay.mid) return "mid";
  return "low";
}

function bucketByMargin(marginPct: number | undefined, thresholds: FinanceMarginDriverThresholds): FinanceProjectMarginBucket {
  if (typeof marginPct !== "number") return "low";
  if (marginPct < 0) return "negative";
  if (marginPct < thresholds.marginPct.low) return "low";
  if (marginPct < thresholds.marginPct.high) return "medium";
  return "high";
}

function getAnalysisBasis(rollup?: FinanceRollupSummary): FinanceProjectMarginAnalysisBasis {
  return typeof rollup?.finalActual?.grossProfit === "number" && (rollup?.finalActual?.taskCount || 0) > 0 ? "final_actual" : "live_current";
}

function deriveProjectMarginSummary(
  input: MarginDriversProjectInput,
  thresholds: FinanceMarginDriverThresholds
): ProjectMarginAnalyticsSummary {
  const shotCount = new Set(input.tasks.map((entry) => entry.task.shotId).filter(Boolean)).size;
  const assetCount = new Set(input.tasks.map((entry) => entry.task.assetId).filter(Boolean)).size;
  const taskCount = input.tasks.length;
  const totalBidValue = input.rollup?.totalBidValueInMoney || 0;
  const bidDays = input.rollup?.totalBidTimeAllocationDays || 0;
  const analysisBasis = getAnalysisBasis(input.rollup);
  const analysisGrossMargin =
    analysisBasis === "final_actual" ? input.rollup?.finalActual?.grossProfit : input.rollup?.blendedLive?.grossProfit;
  const analysisGrossMarginPercent =
    analysisBasis === "final_actual" ? input.rollup?.finalActual?.grossMarginPct : input.rollup?.blendedLive?.grossMarginPct;

  const candidateStarts = [
    parseDate(input.projectStartEst),
    ...input.tasks.map((entry) => parseDate(entry.task.plannedStartDate)).filter(Boolean),
    parseDate(input.bidDate),
  ].filter(Boolean) as Date[];
  const candidateEnds = [
    parseDate(input.projectEndEst),
    ...input.tasks.map((entry) => parseDate(entry.task.plannedEndDate || entry.task.dueDate)).filter(Boolean),
    parseDate(input.updatedAt),
  ].filter(Boolean) as Date[];
  const startDate = candidateStarts.sort((a, b) => a.getTime() - b.getTime())[0];
  const endDate = candidateEnds.sort((a, b) => b.getTime() - a.getTime())[0];
  const projectDurationDays = diffDaysInclusive(startDate, endDate);

  const averageBidRatePerDay = bidDays > 0 ? totalBidValue / bidDays : undefined;
  const impliedRateCardAtBid = roundMoney(averageBidRatePerDay);

  const standardRateCandidates = input.tasks
    .map((entry) => {
      const bidDaysForTask = entry.financialState?.bidTimeAllocationDays || entry.task.bidHours || 0;
      const bidValueForTask = entry.financialState?.taskBidValueInMoney;
      return bidDaysForTask > 0 && typeof bidValueForTask === "number" ? bidValueForTask / bidDaysForTask : undefined;
    })
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const standardRatePerDay = average(standardRateCandidates);
  const discountVsStandardRate =
    typeof impliedRateCardAtBid === "number" && typeof standardRatePerDay === "number" ? impliedRateCardAtBid - standardRatePerDay : undefined;

  const overrunPercents = input.tasks
    .map((entry) => {
      const bidMinutes = entry.financialState?.bidTimeAllocationMinutes || 0;
      const variance = entry.financialState?.varianceVsBidMinutes || 0;
      return bidMinutes > 0 ? (variance / bidMinutes) * 100 : undefined;
    })
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const averageOverrunPercent = roundPercent(average(overrunPercents));

  const tasksWithBidTime = input.tasks.filter((entry) => (entry.financialState?.bidTimeAllocationMinutes || 0) > 0);
  const tasksOverSchedulePercent =
    tasksWithBidTime.length > 0
      ? roundPercent(
          (tasksWithBidTime.filter((entry) => (entry.financialState?.varianceVsBidMinutes || 0) > 0).length / tasksWithBidTime.length) * 100
        )
      : undefined;
  const tasksUnderSchedulePercent =
    tasksWithBidTime.length > 0
      ? roundPercent(
          (tasksWithBidTime.filter((entry) => (entry.financialState?.varianceVsBidMinutes || 0) < 0).length / tasksWithBidTime.length) * 100
        )
      : undefined;

  const artistRates = input.tasks
    .map((entry) => entry.financialState?.assignedArtistDailyRate)
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const averageArtistRate = roundMoney(average(artistRates));

  const assumedVsActualRateVarianceCandidates = input.tasks
    .map((entry) => {
      const assumed = entry.financialState?.departmentAverageDailyRate;
      const actual = entry.financialState?.assignedArtistDailyRate;
      return typeof assumed === "number" && typeof actual === "number" ? actual - assumed : undefined;
    })
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
  const assumedVsActualRateVariance = roundMoney(average(assumedVsActualRateVarianceCandidates));

  const departmentBidMix = new Map<string, number>();
  for (const entry of input.tasks) {
    const department = resolveDepartment(entry.task);
    departmentBidMix.set(department, (departmentBidMix.get(department) || 0) + (entry.financialState?.taskBidValueInMoney || 0));
  }
  const dominantDepartment =
    Array.from(departmentBidMix.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || "Unmapped";

  return {
    projectId: input.projectId,
    projectName: input.projectName,
    currency: input.currency,
    totalBidValue: roundMoney(totalBidValue),
    finalActualLabourCost: roundMoney(input.rollup?.finalActual?.labourCost),
    finalGrossMargin: roundMoney(input.rollup?.finalActual?.grossProfit),
    finalGrossMarginPercent: roundPercent(input.rollup?.finalActual?.grossMarginPct),
    analysisGrossMargin: roundMoney(analysisGrossMargin),
    analysisGrossMarginPercent: roundPercent(analysisGrossMarginPercent),
    analysisBasis,
    projectStartDate: startDate?.toISOString().slice(0, 10),
    projectEndDate: endDate?.toISOString().slice(0, 10),
    projectDurationDays,
    shotCount,
    assetCount,
    taskCount,
    sizeBucket: bucketBySize(totalBidValue, taskCount, thresholds),
    durationBucket: bucketByDuration(projectDurationDays, thresholds),
    rateBucket: bucketByRate(impliedRateCardAtBid, thresholds),
    marginBucket: bucketByMargin(analysisGrossMarginPercent, thresholds),
    averageBidRatePerDay: roundMoney(averageBidRatePerDay),
    impliedRateCardAtBid,
    standardRatePerDay: roundMoney(standardRatePerDay),
    discountVsStandardRate: roundMoney(discountVsStandardRate),
    averageOverrunPercent,
    tasksOverSchedulePercent,
    tasksUnderSchedulePercent,
    averageArtistRate,
    assumedVsActualRateVariance,
    timelogConfidenceScore: input.rollup?.financialConfidenceScore,
    dominantDepartment,
  };
}

function aggregateByBucket(
  projects: ProjectMarginAnalyticsSummary[],
  grouping: FinanceMarginDriverGrouping
) {
  const labels: Record<string, string> = {
    small: "Small",
    medium: "Medium",
    large: "Large",
    short: "Short",
    long: "Long",
    low: "Low Rate",
    mid: "Mid Rate",
    high: "High Rate",
    negative: "Negative Margin",
  };
  const buckets = new Map<string, ProjectMarginAnalyticsSummary[]>();
  for (const project of projects) {
    const key =
      grouping === "size"
        ? project.sizeBucket
        : grouping === "duration"
          ? project.durationBucket
          : grouping === "rate"
            ? project.rateBucket
            : project.dominantDepartment || "Unmapped";
    buckets.set(key, (buckets.get(key) || []).concat(project));
  }

  return Array.from(buckets.entries())
    .map(([bucketId, entries]) => {
      const margins = entries
        .map((entry) => entry.analysisGrossMarginPercent)
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
      const overruns = entries
        .map((entry) => entry.averageOverrunPercent)
        .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
      return {
        bucketId,
        bucketLabel: labels[bucketId] || bucketId,
        projectCount: entries.length,
        totalRevenue: roundMoney(entries.reduce((sum, entry) => sum + (entry.totalBidValue || 0), 0)) || 0,
        averageMarginPercent: roundPercent(average(margins)),
        averageOverrunPercent: roundPercent(average(overruns)),
        marginVariance: variance(margins),
      };
    })
    .sort((a, b) => a.bucketLabel.localeCompare(b.bucketLabel));
}

function buildInsights(projects: ProjectMarginAnalyticsSummary[], analysis: Omit<FinanceMarginDriversAnalysis, "insights">): FinanceMarginDriversInsight[] {
  const insights: FinanceMarginDriversInsight[] = [];
  const lowRate = analysis.averageMarginByRate.find((entry) => entry.bucketId === "low");
  const highRate = analysis.averageMarginByRate.find((entry) => entry.bucketId === "high");
  if (lowRate && highRate && typeof lowRate.averageMarginPercent === "number" && typeof highRate.averageMarginPercent === "number") {
    const delta = roundPercent(highRate.averageMarginPercent - lowRate.averageMarginPercent);
    if (typeof delta === "number" && Math.abs(delta) >= 2) {
      insights.push({
        id: "rate-bucket-delta",
        tone: delta < 0 ? "warning" : "neutral",
        summary: `Projects in the ${delta > 0 ? "highest" : "lowest"} rate bucket show ${Math.abs(delta)}% ${delta > 0 ? "higher" : "lower"} margin on average.`,
        helperText: "This compares effective bid rate per day across the current active project set.",
      });
    }
  }

  const small = analysis.averageMarginBySize.find((entry) => entry.bucketId === "small");
  const large = analysis.averageMarginBySize.find((entry) => entry.bucketId === "large");
  if (small && large && typeof small.averageMarginPercent === "number" && typeof large.averageMarginPercent === "number") {
    const delta = roundPercent(large.averageMarginPercent - small.averageMarginPercent);
    if (typeof delta === "number" && Math.abs(delta) >= 2) {
      insights.push({
        id: "size-bucket-delta",
        tone: delta < 0 ? "warning" : "neutral",
        summary: `${delta > 0 ? "Larger" : "Smaller"} projects are averaging ${Math.abs(delta)}% ${delta > 0 ? "higher" : "lower"} margin.`,
        helperText: "Size is currently derived from bid value and task-count thresholds rather than a separate commercial classification.",
      });
    }
  }

  if (typeof analysis.overrunMarginCorrelation === "number") {
    const abs = Math.abs(analysis.overrunMarginCorrelation);
    if (abs >= 0.25) {
      insights.push({
        id: "overrun-correlation",
        tone: analysis.overrunMarginCorrelation < 0 ? "warning" : "positive",
        summary:
          analysis.overrunMarginCorrelation < 0
            ? "Projects with higher overruns are currently correlating with weaker margin."
            : "Projects with higher overruns are not currently showing weaker margin.",
        helperText: `Correlation between average overrun % and margin % is ${analysis.overrunMarginCorrelation}.`,
      });
    }
  }

  const mostVariantBucket = [...analysis.averageMarginByDuration]
    .filter((entry) => typeof entry.marginVariance === "number")
    .sort((a, b) => (b.marginVariance || 0) - (a.marginVariance || 0))[0];
  if (mostVariantBucket && (mostVariantBucket.marginVariance || 0) > 10) {
    insights.push({
      id: "duration-variance",
      tone: "warning",
      summary: `${mostVariantBucket.bucketLabel} projects show the widest margin spread.`,
      helperText: "High variance often means this bucket contains a mix of both healthy and troubled delivery patterns.",
    });
  }

  if (!insights.length && projects.length > 0) {
    insights.push({
      id: "baseline-insight",
      tone: "neutral",
      summary: "Margin drivers are now available for comparison across size, duration, rate, and department mix.",
      helperText: "As more projects reach stronger final-actual coverage, these comparisons should become more diagnostic.",
    });
  }

  return insights;
}

// This service is intentionally analytical rather than financial truth. It consumes the
// existing project rollups and task states, then derives comparison attributes that can be
// swapped out later for predictive modelling or richer pricing analysis without changing
// the underlying finance engine.
export function buildFinanceMarginDriversAnalysis(
  inputs: MarginDriversProjectInput[],
  thresholds: FinanceMarginDriverThresholds = DEFAULT_MARGIN_DRIVER_THRESHOLDS
): FinanceMarginDriversAnalysis {
  const projects = inputs.map((input) => deriveProjectMarginSummary(input, thresholds));
  const averageMarginBySize = aggregateByBucket(projects, "size");
  const averageMarginByDuration = aggregateByBucket(projects, "duration");
  const averageMarginByRate = aggregateByBucket(projects, "rate");
  const averageMarginByDepartmentMix = aggregateByBucket(projects, "department_mix");

  const correlationInput = projects.filter(
    (project) => typeof project.averageOverrunPercent === "number" && typeof project.analysisGrossMarginPercent === "number"
  );
  const overrunMarginCorrelation = correlation(
    correlationInput.map((project) => project.averageOverrunPercent as number),
    correlationInput.map((project) => project.analysisGrossMarginPercent as number)
  );

  const baseAnalysis = {
    thresholds,
    projects,
    averageMarginBySize,
    averageMarginByDuration,
    averageMarginByRate,
    averageMarginByDepartmentMix,
    overrunMarginCorrelation,
  };

  return {
    ...baseAnalysis,
    insights: buildInsights(projects, baseAnalysis),
  };
}

// TODO: Extend this layer with weekly/monthly trend slices once we add longitudinal
// finance snapshots rather than relying on today's latest-state reconstruction.
// TODO: Add forecast-vs-actual comparison drivers so pricing pressure can be separated
// from delivery overrun more explicitly.
// TODO: Add filters for shot / sequence / task status once managers need narrower cuts.
// TODO: Add exportable margin-driver reports once the company view stabilizes.
